import { useCallback, useContext, useMemo, useState } from 'react';

import {
  AtomMap,
  itemsInStack,
  MarkerLayer,
  useDefaultMapLocation,
} from '@atom/map';
import { BoundingBoxSubjectType, MapIdleEvent } from '@atom/types/map';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import LocationsAndAssetsContext from '../LocationsAndAssetsContext';

import { useTaskAssetMapData } from './useTaskAssetMapData';

const LOCATION_MAP_ZOOM = 14;

type TaskAssetMapProps = {
  cartAssetIds: string[];
  taskAssetIds?: string[];
  onAssetClick: (id: string) => void;
  hoverId?: string;
};

const TaskAssetMap = ({
  cartAssetIds,
  taskAssetIds = [],
  onAssetClick,
  hoverId = '',
}: TaskAssetMapProps) => {
  const { workOrderDetail, task } = useContext(LocationsAndAssetsContext);
  const defaultLocation = useDefaultMapLocation();

  const [viewport, setViewport] = useState<MapIdleEvent | null>(null);
  const [mapData, loading] = useTaskAssetMapData({
    coordinates: viewport?.coordinates ?? null,
    zoomLevel: viewport?.zoom ?? null,
    schemaIds: task?.allowedMultiAssetSchemaIds ?? [],
  });

  const activeId = hoverId || cartAssetIds.join(',');
  const disabledIds = useMemo(() => new Set(taskAssetIds), [taskAssetIds]);

  const handleAssetGroupClick = useCallback(
    (stackKey: string) => {
      const ids = itemsInStack(mapData.assets ?? [], stackKey)
        .map(asset => asset.id)
        .join(',');

      onAssetClick(ids);
    },
    [mapData.assets, onAssetClick],
  );

  const handleMarkerClick = useCallback(
    (id: string) => onAssetClick(id),
    [onAssetClick],
  );

  return (
    <AtomMap
      initialViewState={{
        center: defaultLocation.center,
        zoom: LOCATION_MAP_ZOOM,
      }}
      onIdle={setViewport}
      compactToolbars
      loading={loading}
      frameTask={{
        subjectType: BoundingBoxSubjectType.ASSET,
        taskId: task?.id,
        workOrderId: workOrderDetail?.id,
        active: !isNilOrEmpty(taskAssetIds),
        frameKey: taskAssetIds.join(','),
      }}
    >
      <MarkerLayer
        data={mapData}
        activeId={activeId}
        disabledIds={disabledIds}
        onAssetGroupClick={handleAssetGroupClick}
        onMarkerClick={handleMarkerClick}
      />
    </AtomMap>
  );
};

export default TaskAssetMap;
